import React, { useState } from 'react';
import { Star, ThumbsUp, MessageSquare, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useReviews } from '@/hooks/useReviews';
import WriteReviewDialog from '@/pages/entity/WriteReviewDialog';

const Stars = ({ value, size = 'h-4 w-4' }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`${size} ${n <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`}
      />
    ))}
  </div>
);

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

/**
 * Reviews section for an EntityDetail page.
 *
 * Props
 *  - entityId, entityName     — required
 *  - currentUser              — current viewer (null if logged out)
 *  - onAggregateChange?       — bubble new {rating, review_count} up to parent
 */
const ReviewsSection = ({ entityId, entityName, currentUser, onAggregateChange }) => {
  const [sort, setSort] = useState('recent');
  const r = useReviews(entityId, { sort, onAggregateChange });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isLoggedIn = !!currentUser;
  const isAdmin = !!currentUser?.is_admin;

  const openWrite = () => {
    setEditingReview(null);
    setDialogOpen(true);
  };
  const openEdit = (review) => {
    setEditingReview(review);
    setDialogOpen(true);
  };
  const handleDialogSubmit = async (payload) => {
    if (editingReview) {
      return r.update(editingReview.review_id, payload);
    }
    return r.create(payload);
  };

  const { summary, items, viewerReview } = r;
  const total = summary.total || 0;

  return (
    <section
      className="bg-card rounded-2xl border border-border/50 p-6 space-y-5"
      data-testid="reviews-section"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Reviews</h2>
        </div>
        {isLoggedIn && !viewerReview && (
          <Button onClick={openWrite} data-testid="reviews-write-btn">
            Write a review
          </Button>
        )}
        {!isLoggedIn && (
          <p className="text-xs text-muted-foreground" data-testid="reviews-login-cta">
            Log in to write a review
          </p>
        )}
      </div>

      {/* Summary header */}
      <div
        className="grid sm:grid-cols-[auto_1fr] gap-6 items-center"
        data-testid="reviews-summary"
      >
        <div className="text-center sm:text-left">
          <p className="text-4xl font-bold leading-none" data-testid="reviews-average">
            {summary.average ? summary.average.toFixed(1) : '—'}
          </p>
          <div className="mt-1 inline-block">
            <Stars value={summary.average || 0} size="h-5 w-5" />
          </div>
          <p className="text-xs text-muted-foreground mt-1" data-testid="reviews-total">
            {total} review{total === 1 ? '' : 's'}
          </p>
        </div>

        {/* Breakdown bars */}
        <div className="space-y-1.5" data-testid="reviews-breakdown">
          {[5, 4, 3, 2, 1].map((n) => {
            const count = summary.breakdown?.[String(n)] || 0;
            const pct = total > 0 ? (count / total) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-3 text-muted-foreground">{n}</span>
                <Star className="h-3 w-3 fill-amber-400 text-amber-400 shrink-0" />
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-amber-400 transition-all"
                    style={{ width: `${pct}%` }}
                    data-testid={`reviews-bar-${n}`}
                  />
                </div>
                <span className="w-8 text-right text-muted-foreground tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Your review banner */}
      {viewerReview && (
        <div
          className="p-3 rounded-xl bg-primary/5 border border-primary/20 flex items-start gap-3"
          data-testid="reviews-viewer-banner"
        >
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary mb-1">Your review</p>
            <Stars value={viewerReview.rating} />
            {viewerReview.title && (
              <p className="font-medium text-sm mt-1">{viewerReview.title}</p>
            )}
            <p className="text-sm text-muted-foreground line-clamp-2">{viewerReview.body}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openEdit(viewerReview)}
              data-testid="reviews-edit-own-btn"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setConfirmDelete(viewerReview)}
              data-testid="reviews-delete-own-btn"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Sort + list */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'Be the first to share your experience.' : `Showing ${items.length} of ${total}`}
        </p>
        {total > 1 && (
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[160px] h-8" data-testid="reviews-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent" data-testid="reviews-sort-recent">Most recent</SelectItem>
              <SelectItem value="helpful" data-testid="reviews-sort-helpful">Most helpful</SelectItem>
              <SelectItem value="rating" data-testid="reviews-sort-rating">Highest rated</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <ul className="space-y-4" data-testid="reviews-list">
        {items.length === 0 && (
          <li
            className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border/50 rounded-xl"
            data-testid="reviews-empty"
          >
            No reviews yet — yours could be the first.
          </li>
        )}
        {items.map((rev) => {
          const isOwn = currentUser?.user_id === rev.user_id;
          return (
            <li
              key={rev.review_id}
              className="p-4 rounded-xl bg-muted/30 border border-border/50 space-y-2"
              data-testid={`review-row-${rev.review_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {rev.author?.picture ? (
                    <img src={rev.author.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-medium text-primary">
                      {(rev.author?.name || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{rev.author?.name || 'Explorer'}</p>
                    <Stars value={rev.rating} />
                    <span className="text-xs text-muted-foreground">{formatDate(rev.created_at)}</span>
                  </div>
                  {rev.title && <p className="font-medium text-sm mt-1">{rev.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{rev.body}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-12">
                <Button
                  size="sm"
                  variant={rev.viewer_marked_helpful ? 'default' : 'ghost'}
                  onClick={() => r.toggleHelpful(rev.review_id)}
                  disabled={isOwn || !isLoggedIn || r.busyId === rev.review_id}
                  className="h-7 text-xs"
                  data-testid={`review-helpful-${rev.review_id}`}
                >
                  <ThumbsUp className="h-3 w-3 mr-1" />
                  Helpful · {rev.helpful_count || 0}
                </Button>
                {isAdmin && !isOwn && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDelete(rev)}
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    data-testid={`review-admin-delete-${rev.review_id}`}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Admin: hide
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <WriteReviewDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entityName={entityName}
        existing={editingReview}
        onSubmit={handleDialogSubmit}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="reviews-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.user_id === currentUser?.user_id
                ? "This will permanently remove your review and your rating contribution."
                : "Removing this review will recompute the partner's average rating."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reviews-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirmDelete) {
                  await r.remove(confirmDelete.review_id);
                  setConfirmDelete(null);
                }
              }}
              data-testid="reviews-delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default ReviewsSection;
